import { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Card, ListGroup, Badge, Button } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/styles.css';
import '../styles/QueueAnimations.css'

const Queue = () => {
  const [queue, setQueue] = useState<any[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [animateNowSinging, setAnimateNowSinging] = useState(false);
  const [breakStatus, setBreakStatus] = useState<{
    isActive: boolean;
    timeRemainingSeconds: number;
  }>({ isActive: false, timeRemainingSeconds: 0 });

  useEffect(() => {
    const fetchQueue = () => {
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/songrequests/queue`)
        .then(res => {
          if (queue.length > 0 && res.data.length > 0 && queue[0].id !== res.data[0].id) {
            setAnimateNowSinging(true);
            setTimeout(() => setAnimateNowSinging(false), 4000); // Increased to 4s for more dramatic effect
          }
          setQueue(res.data);
        })
        .catch(err => console.error('Failed to fetch queue:', err));
    };
    const fetchBreakStatus = () => {
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/breaks/status`)
        .then(res => {
          setBreakStatus({
            isActive: res.data.isActive,
            timeRemainingSeconds: res.data.timeRemainingSeconds || 0
          });
        })
        .catch(err => console.error('Failed to fetch break status:', err));
    };
    fetchQueue();
    fetchBreakStatus();
    const queueInterval = setInterval(fetchQueue, 5000);
    const breakInterval = setInterval(fetchBreakStatus, 1000); // Update every second for countdown
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      clearInterval(queueInterval);
      clearInterval(breakInterval);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [queue]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error('Error attempting to enable full-screen mode:', err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const formatCountdown = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const requestFormUrl = `itsallkaraoke.com`;

  return (
    <Container fluid className="mt-4 mb-4 card-pulse" style={{ minHeight: '100vh' }}>
      <Card className="shadow-lg border-0 card-custom glow-effect" style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '15px' }}>
        <Card.Header
          className="text-center py-4 position-relative header-flow"
          style={{
            color: 'white',
            borderRadius: '15px 15px 0 0',
          }}
        >
          <div className="mb-3 animate__animated animate__fadeIn">
            <img
              src="/logo.png"
              alt="Karaoke Band Logo"
              className="logo-static"
              style={{
                maxWidth: '150px',
                maxHeight: '100px',
                objectFit: 'contain'
              }}
            />
          </div>
          <h1
            className="mb-0 glow-effect"
            style={{
              fontFamily: "'blowbrush', sans-serif",
              letterSpacing: '1px',
              fontSize: '3.5rem'
            }}
          >
            KARAOKE QUEUE
          </h1>
          <div
            className="position-absolute end-0 top-0 p-3 animate__animated animate__fadeIn"
            style={{ animationDelay: '0.6s' }}
          >
            <div className="text-center">
              <h6 className="fw-bold text-white mb-2" style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '1rem' }}>
                Request a Song!
              </h6>
              <div className="d-inline-block p-2 bg-white rounded-3 shadow-sm glow-effect">
                <QRCodeSVG
                  value={requestFormUrl}
                  size={120}
                  bgColor="#ffffff"
                  fgColor="#d63384"
                  level="H"
                  includeMargin={true}
                />
              </div>
              <p className="mt-1 text-white" style={{ fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif'" }}>
                Scan to join!
              </p>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-5 position-relative">
          {!isFullscreen && (
            <Button
              variant="primary"
              className="position-absolute top-0 start-0 m-3 animate__animated animate__fadeIn fullscreen-btn-pulse"
              onClick={toggleFullscreen}
              style={{
                zIndex: 1000,
                backgroundColor: '#4ecdc4',
                borderColor: '#4ecdc4',
                transition: 'all 0.3s'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#45b7b0'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#4ecdc4'}
            >
              Full Screen
            </Button>
          )}

          {breakStatus.isActive ? (
            <>
              <div className="text-center mb-5 animate__animated animate__fadeIn now-singing-sparkle" style={{ marginBottom: '20px', position: 'relative' }}>
                <h2 className="display-5 fw-bold text-dark mb-3 glow-effect" style={{ fontFamily: "'Montserrat', sans-serif'" }}>
                  We're Taking a Break!
                </h2>
                <div
                  className="p-4 rounded-3 shadow-sm break-message"
                  style={{ backgroundColor: '#fff3cd', border: '2px solid #ffd700' }}
                >
                  <h3
                    className="fw-semibold"
                    style={{ color: '#d63384', fontSize: '1.8rem' }}
                  >
                    But <span className="its-all-good">It's All Good</span>, We'll Be Back In...
                  </h3>
                  <p className="mb-0 countdown-timer" style={{ fontSize: '2rem', color: '#343a40', fontWeight: 'bold' }}>
                    {formatCountdown(breakStatus.timeRemainingSeconds)}
                  </p>
                </div>
              </div>

              {queue.length > 0 && (
                <div className="text-center animate__animated animate__fadeIn" style={{ animationDelay: '0.3s' }}>
                  <h4 className="fw-bold text-uppercase mb-4 glow-effect" style={{ color: '#495057', fontFamily: "'Montserrat', sans-serif'" }}>
                    Up Next
                  </h4>
                  <div 
                    style={{ 
                      maxHeight: 'calc(100vh - 400px)',
                      overflowY: 'auto',
                      paddingRight: '10px'
                    }}
                  >
                    <ListGroup
                      variant="flush"
                      className="mx-auto shadow-sm"
                      style={{
                        maxWidth: '700px',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                      }}
                    >
                      {queue.map((req: any, index: number) => (
                        <ListGroup.Item
                          key={req.id}
                          className="py-3 d-flex justify-content-between align-items-center animate__animated animate__slideInUp list-item-glow"
                          style={{
                            animationDelay: `${0.1 * (index + 1)}s`,
                            borderBottom: '1px solid #e9ecef'
                          }}
                        >
                          <span style={{ fontSize: '1.2rem', color: '#212529' }}>
                            {req.name} - <Badge bg="secondary" className="me-2">{req.song.artist.name}</Badge>
                            <span className="fw-medium">{req.song.title}</span>
                          </span>
                          <Badge bg="success" pill className="badge-glow">#{index + 1}</Badge>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                </div>
              )}
            </>
          ) : queue.length > 0 ? (
            <>
              <div className="text-center mb-5 animate__animated animate__fadeIn now-singing-sparkle" style={{ marginBottom: '20px', position: 'relative' }}>
                <h2 className="display-5 fw-bold text-dark mb-3 glow-effect" style={{ fontFamily: "'Montserrat', sans-serif'" }}>
                  Now Singing
                </h2>
                <div
                  className={`p-4 rounded-3 shadow-sm ${animateNowSinging ? 'dramatic-spotlight' : ''}`}
                  style={{ backgroundColor: '#fff3cd', border: '2px solid #ffd700' }}
                >
                  <h3
                    className={`fw-semibold ${animateNowSinging ? 'spin-and-grow' : ''}`}
                    style={{ color: '#d63384', fontSize: '1.8rem' }}
                  >
                    {queue[0].name}
                  </h3>
                  <p className="mb-0" style={{ fontSize: '1.4rem', color: '#343a40' }}>
                    <Badge bg="info" className="me-2">{queue[0].song.artist.name}</Badge>
                    <span className="fw-bold">{queue[0].song.title}</span>
                  </p>
                </div>
              </div>

              <div className="text-center animate__animated animate__fadeIn" style={{ animationDelay: '0.3s' }}>
                <h4 className="fw-bold text-uppercase mb-4 glow-effect" style={{ color: '#495057', fontFamily: "'Montserrat', sans-serif'" }}>
                  Up Next
                </h4>
                <div 
                  style={{ 
                    maxHeight: 'calc(100vh - 400px)',
                    overflowY: 'auto',
                    paddingRight: '10px'
                  }}
                >
                  <ListGroup
                    variant="flush"
                    className="mx-auto shadow-sm"
                    style={{
                      maxWidth: '700px',
                      backgroundColor: 'white',
                      borderRadius: '10px',
                    }}
                  >
                    {queue.slice(1).map((req: any, index: number) => (
                      <ListGroup.Item
                        key={req.id}
                        className="py-3 d-flex justify-content-between align-items-center animate__animated animate__slideInUp list-item-glow"
                        style={{
                          animationDelay: `${0.1 * (index + 1)}s`,
                          borderBottom: '1px solid #e9ecef'
                        }}
                      >
                        <span style={{ fontSize: '1.2rem', color: '#212529' }}>
                          {req.name} - <Badge bg="secondary" className="me-2">{req.song.artist.name}</Badge>
                          <span className="fw-medium">{req.song.title}</span>
                        </span>
                        <Badge bg="success" pill className="badge-glow">#{index + 1}</Badge>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-5 animate__animated animate__pulse">
              <h3 className="text-muted fw-light glow-effect" style={{ fontFamily: "'Poppins', sans-serif'", fontSize: '1.6rem' }}>
                Queue is Empty - Grab the Mic!
              </h3>
              <p className="text-secondary">Request your song now to kick off the night!</p>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Queue;